import InputAdornment from '@mui/material/InputAdornment';
import { type Theme, useTheme } from '@mui/material/styles';
import MUITextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import EyeIcon from '../../assets/eye.svg?react';
import EyeSlashIcon from '../../assets/eyeSlash.svg?react';
import HelperText from '../HelperText/HelperText';
import { IconButton } from '../IconButton';
import type { TextFieldProps } from './TextField.model';
import { generateTextFieldCss } from './TextField.styles';

/* Component function to generate a MUI Text Field with custom styling. */
const StyledTextField = styled(MUITextField).withConfig({
  shouldForwardProp: (prop) =>
    ![
      'error',
      'errorLabel',
      'heapRedactText',
      'helperTextLabel',
      'input',
      'maxCharacterCount',
      'multilineHeight',
      'sensitive',
      'redactFromHeap',
      'showCharacterCount',
      'startIcon',
      'endIcon',
      'testId',
      'theme',
    ].includes(prop),
})<TextFieldProps>`
  ${({ theme, ...props }) => generateTextFieldCss(theme, props)}
`;

/* BD Design System Text Field Component */
const TextField: React.FC<TextFieldProps> = ({
  name,
  disabled = false,
  error = false,
  errorLabel = '',
  maxCharacterCount = 1024,
  multiline = false,
  multilineHeight = '250px',
  showCharacterCount = false,
  sensitive = false,
  redactFromHeap = false,
  startIcon = undefined,
  endIcon = undefined,
  value,
  ...props
}) => {
  const theme = useTheme<Theme>();
  const normalizedValue = value ?? '';
  const [hideValue, setHideValue] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [blockFocusStyles, setBlockFocusStyles] = useState<boolean>(false);
  const displayErrorLabel = useMemo(
    () => error && !isFocused,
    [error, isFocused]
  );

  const PrivacyButton = useMemo(
    () =>
      hideValue ? (
        <IconButton
          aria-label={'display input'}
          disabled={disabled}
          onClick={() => setHideValue(false)}
          icon={<EyeIcon />}
          data-testid={`${name}-privacy-button--show`}
          size={'small'}
        />
      ) : (
        <IconButton
          aria-label={'hide input'}
          disabled={disabled}
          onClick={() => setHideValue(true)}
          icon={<EyeSlashIcon />}
          data-testid={`${name}-privacy-button--hide`}
          size={'small'}
        />
      ),
    [hideValue, disabled, name]
  );

  /* Helper function to clean up style class name generation */
  const generateTextFieldClassName = (): string => {
    const errorClass = displayErrorLabel ? 'error' : '';
    const disabledClass = disabled ? 'disabled' : '';
    const hasIconClass = startIcon ? 'hasIcon' : '';
    const sensitiveClass = sensitive ? 'sensitive' : '';
    const noFocusClass = blockFocusStyles ? 'noFocus' : '';

    return [
      props.className ?? '',
      errorClass,
      disabledClass,
      hasIconClass,
      sensitiveClass,
      noFocusClass,
    ]
      .join(' ')
      .trim();
  };

  const inputType = multiline
    ? undefined
    : sensitive && hideValue
      ? 'password'
      : 'text';

  return (
    <StyledTextField
      {...props}
      value={normalizedValue}
      className={generateTextFieldClassName()}
      disabled={disabled}
      error={displayErrorLabel}
      label={<Typography variant="small_body_medium">{props.label}</Typography>}
      name={name}
      size="small"
      theme={theme}
      type={inputType}
      multiline={multiline}
      multilineHeight={multilineHeight}
      variant="filled"
      onFocus={(event) => {
        props.onFocus?.(event);
        setIsFocused(true);
      }}
      onMouseDown={(event) => {
        props.onMouseDown?.(event);
        setBlockFocusStyles(true);
      }}
      onBlur={(event) => {
        props.onBlur?.(event);
        setIsFocused(false);
        setBlockFocusStyles(false);
      }}
      helperText={
        <HelperText
          error={displayErrorLabel}
          currentCharacterCount={normalizedValue.length}
          label={displayErrorLabel ? errorLabel : props.helperTextLabel}
          maxCharacterCount={maxCharacterCount}
          showCharacterCount={showCharacterCount}
          testId={name}
        />
      }
      /* Inner Component Prop Overrides */
      FormHelperTextProps={{ component: 'div' }}
      InputLabelProps={{ shrink: !!normalizedValue || isFocused }}
      InputProps={{
        ...props.InputProps,
        startAdornment: startIcon && (
          <InputAdornment position="start" sx={{ margin: '0 !important' }}>
            {startIcon}
          </InputAdornment>
        ),
        endAdornment:
          endIcon || sensitive ? (
            <InputAdornment position="end">
              {endIcon && endIcon}
              {sensitive && PrivacyButton}
            </InputAdornment>
          ) : (
            props.InputProps?.endAdornment
          ),
      }}
      inputProps={{
        ...props.inputProps,
        maxLength: maxCharacterCount,
      }}
      /* Data attributes */
      data-heap-redact-text={sensitive || redactFromHeap}
    />
  );
};

export default TextField;
